import React, { useState } from 'react';
import { InputNumber, Button, Form, Table, Popconfirm, Space, Tag } from 'antd';
import {
  MinusCircleOutlined,
  PlusCircleOutlined,
  UndoOutlined,
} from '@ant-design/icons';

import MoneyTitle from '../customer/MoneyTitle';
import StrikeTeamModel from '../../models/StrikeTeamModel';

const limits = {
  min: 1,
  max: 1000,
  countMin: 2,
  countMax: 5,
};

const TeamEditorValues = ({ teamId, team, defaultTag, onLoading }) => {
  const { values: teamValues, percents, defaultCurrency, rgbColor } = team || {};
  const { updatedValuesAt, updatedPercentsAt, selectedValueByIndex } = team || {};
  const countValues = teamValues ? teamValues.length : 0;
  const midIndex = Math.floor(countValues / 2);
  const useSelectedValueByIndex = selectedValueByIndex === undefined ? midIndex : selectedValueByIndex;
  const defaultSelectedValueByIndex = useSelectedValueByIndex > countValues ? countValues : useSelectedValueByIndex;

  const [form] = Form.useForm();
  const [values, setValues] = useState(teamValues || []);
  const [newValue, setNewValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isChange, setIsChange] = useState(false);
  const [defaultOption, setDefaultOption] = useState(defaultSelectedValueByIndex); // Default option state
  
  const model = new StrikeTeamModel({ teamId });
  const tagColor = '#555';
  
  const displayValues = values;
  
  const handleSave = async ({ values, selectedValueByIndex }) => {
    setLoading(true);
    onLoading && onLoading({ message: `Saving values ...` });
    const sortedValues = values.sort((a, b) => a - b);
    const savedValues = sortedValues.length > 0 ? sortedValues : null;
    await model.saveTeamValues({ values: savedValues, selectedValueByIndex });
    onLoading && onLoading({ message: null });
    setLoading(false);
    setIsChange(false);
  };

  const handleClear = () => {
    const { values } = defaultTag || {};
    setValues(values || []);
    setIsChange(true);
  }

  const handleAddValue = (amount) => {
    setValues([...values, amount * 100]); // Store as cents
    setIsChange(true);
  };

  const handleDeleteValue = (amount) => {
    setValues(values.filter((v) => v !== amount));
    setIsChange(true);
  };

  const handleUpdateValue = (position, nextValue) => {
    if (nextValue === null || nextValue === undefined) return;
    const nextAmount = Math.round(parseFloat(nextValue) * 100);
    if (Number.isNaN(nextAmount)) return;
    const isInvalid = nextValue < limits.min || nextValue > limits.max;
    if (isInvalid) return;
    const updatedValues = [...values];
    if (updatedValues[position] === nextAmount) return;
    if (updatedValues.includes(nextAmount)) return;
    updatedValues[position] = nextAmount;
    setValues(updatedValues);
    setIsChange(true);
  };

  const handleFinish = (formData) => {
    const { amount } = formData;
    if (amount) {
      handleAddValue(parseFloat(amount));
    }
    form.resetFields();
  };

  const handleSelectedDefaultRow = (index) => {
    setDefaultOption(index);
    setIsChange(true);
  }

  const isAddDisabled = () => {
    const newValueCent = parseFloat(newValue) * 100;
    const isInValues = values.includes(newValueCent);
    return values.length >= limits.countMax || newValue < limits.min || newValue > limits.max || isInValues;
  }

  const onChangeNewValue = (v) => {
    setNewValue(v);
  }

  const columns = [
    {
      title: `Pre-set Amounts (min: ${limits.countMin}, max: ${limits.countMax})`,
      dataIndex: 'amount',
      key: 'amount',
      render: (text, record) =>
        record.type === 'value' ?
          <div className='d-flex align-items-center justify-content-between'>
            <div style={{ flexGrow: 1 }}></div>
            <InputNumber
              min={limits.min}
              max={limits.max}
              step={1}
              value={record.amount / 100}
              onChange={(value) => handleUpdateValue(record.position, value)}
              onFocus={(event) => event.target.select()}
              style={{ minWidth: '120px', textAlign: 'center', fontWeight: 'bold', fontSize: '16px' }}
              formatter={(value) => (value === undefined || value === null ? '' : `${defaultCurrency === 'USD' ? '$' : defaultCurrency === 'GBP' ? '£' : '€'}${value}`)}
              parser={(value) => value.replace(/[€£$,\s]/g, '')}
              controls={false}
            />
            <div className='mx-2 my-0'>
              <Popconfirm
                title={`Are you sure you want to delete?`}
                onConfirm={() =>
                  handleDeleteValue(record.amount)
                }
              >
                <Button
                  type="danger" icon={<MinusCircleOutlined />}
                />
              </Popconfirm>
            </div>
            {/* Spacer Div */}
            <div style={{ flexGrow: 1 }}></div>
            <Button
              type={record.position === defaultOption ? "primary" : "ghost"}
              onClick={() => handleSelectedDefaultRow(record.position)}
              style={{ minWidth: '120px', fontWeight: 'bold', fontSize: '12px' }}
            >
              {record.position === defaultOption ? "Default" : "Make default"}
            </Button>
            <div style={{ flexGrow: 1 }}></div>
          </div>
          :
            <div className='d-flex align-items-center justify-content-between'>
              <div style={{ flexGrow: 1 }}></div>
              <Form layout="inline" form={form} onFinish={handleFinish}>
                  <Form.Item name="amount">
                    <InputNumber
                      min={limits.min}
                      max={limits.max}
                      step={1}
                      value={newValue}
                      onChange={onChangeNewValue}
                      onFocus={(event) => event.target.select()}
                      placeholder="Add amount"
                      style={{ minWidth: '120px' }}
                      formatter={(value) => (value === undefined || value === null ? '' : `${defaultCurrency === 'USD' ? '$' : defaultCurrency === 'GBP' ? '£' : '€'}${value}`)}
                      parser={(value) => value.replace(/[€£$,\s]/g, '')}
                      controls={false}
                    />
                  </Form.Item>
                  <div style={{ width: '40px'}}></div>
                  <Form.Item>
                    <Button type="primary" htmlType="submit" disabled={isAddDisabled()} icon={<PlusCircleOutlined />}>Add</Button>
                  </Form.Item>
                </Form>
              <div style={{ flexGrow: 1 }}></div>
            </div>
      ,
    },
  ];

  const data = [
    ...displayValues.map((v, i) => ({ key: `value-${i}`, type: 'value', amount: v, position: i  })),
    { key: 'value-add-new', type: 'add', amount: 0, position: displayValues.length },
  ];

  const isSaveDisabled = () => {
    if (!isChange) {
      return true;
    }
    if (values.length === 0) {
      return false;
    }
    return values.length < limits.countMin || values.length > limits.countMax;
  }

  return (
    <div className="container mt-4">
      <h3>Set up Pre-set Amounts</h3>

      {percents.length > 0 && (
        <div className="mt-4 text-danger">
          Percentages overide values. Please clear all percentages to use values.
        </div>
      )}

      <Table
        className="mt-4"
        columns={columns}
        dataSource={data}
        pagination={false}
        bordered
      />

      <Button
        type="primary"
        onClick={() => handleSave({ values, selectedValueByIndex: defaultOption })}
        style={{ marginTop: '12px', marginLeft: '-24px' }}
        loading={loading}
        disabled={isSaveDisabled()}
      >
        {'Save Changes'}
      </Button>

      <Button
        onClick={() => handleClear()}
        style={{ marginTop: '12px', marginLeft: '16px' }}
        loading={loading}
        icon={<UndoOutlined />}
      >
        {'Use Defaults'}
      </Button>

      {updatedValuesAt && (
        <div className="mt-0">
          <small>Last updated: {updatedValuesAt.toDate().toLocaleString()}</small>
        </div>
      )}
      { updatedPercentsAt && percents.length > 0 && (
        <div className="mt-0">
          {percents.length > 0 && <small>Percentages overide values.</small>}
          {percents.length > 0 && <small> </small>}
          <small>Last updated: {updatedPercentsAt.toDate().toLocaleString()}</small>
        </div>
      )}

    </div>
  );
};

export default TeamEditorValues;
